import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import signalRService from '../../../utilities/WS/SignalRService';
import {
  GetAutodetectedTimezone,
  ConvertLocalToUtc,
  GetPresetRange,
  ValidateDateRange,
  DownloadMessagesExcel,
  DownloadFanCamZip,
  RequestMasterZip,
  DownloadReadyReport,
  GetMasterZipStatus,
} from '../../../utilities/BL/Admin/Reports';
import DateRangeFilter from './Components/DateRangeFilter';
import TimezoneSelector from './Components/TimezoneSelector';
import ReportCard from './Components/ReportCard';

export default function ReportsManager({ getOutOfTheDynamic }) {
  const { showMessage } = useFloatMessage();

  // Detección inicial de la zona horaria del cliente
  const detectedTimezone = useMemo(() => GetAutodetectedTimezone(), []);

  // Estado centralizado de filtros para el módulo de reportes (HU-02)
  const [filterState, setFilterState] = useState(() => {
    const initialTz = detectedTimezone;
    const initialPreset = 'today';
    const range = GetPresetRange(initialPreset, initialTz);

    return {
      mode: 'match', // 'all' (todo lo acumulado) | 'match' (filtrar por partido)
      timezone: initialTz,
      preset: initialPreset, // 'today' | 'yesterday' | 'last6hours' | 'custom'
      startLocal: range.startLocal,
      endLocal: range.endLocal,
      startUtc: range.startUtc,
      endUtc: range.endUtc,
      isValid: true,
    };
  });

  /**
   * Manejador del cambio de modo ('all' vs 'match')
   */
  const handleModeChange = useCallback((newMode) => {
    setFilterState((prev) => {
      if (newMode === 'all') {
        return {
          ...prev,
          mode: 'all',
          isValid: true,
        };
      }

      const validation = ValidateDateRange(prev.startUtc, prev.endUtc);
      return {
        ...prev,
        mode: 'match',
        isValid: validation.isValid,
      };
    });
  }, []);

  /**
   * Manejador del cambio de zona horaria en el dropdown
   */
  const handleTimezoneChange = useCallback(
    (newTz) => {
      setFilterState((prev) => {
        if (prev.preset === 'custom') {
          // Recalcular marcas UTC manteniendo los valores locales vigentes
          const startUtc = ConvertLocalToUtc(prev.startLocal, newTz);
          const endUtc = ConvertLocalToUtc(prev.endLocal, newTz);
          const validation = ValidateDateRange(startUtc, endUtc);

          if (!validation.isValid) {
            showMessage({
              type: MESSAGE_TYPE.WARNING,
              text: validation.message || 'El rango de fechas no es válido en la nueva zona horaria.',
            });
          }

          return {
            ...prev,
            timezone: newTz,
            startUtc,
            endUtc,
            isValid: validation.isValid,
          };
        }

        // Para presets predefinidos, regenerar rango acorde a la fecha local de la nueva zona
        const range = GetPresetRange(prev.preset, newTz);
        return {
          ...prev,
          timezone: newTz,
          startLocal: range.startLocal,
          endLocal: range.endLocal,
          startUtc: range.startUtc,
          endUtc: range.endUtc,
          isValid: true,
        };
      });
    },
    [showMessage]
  );

  /**
   * Manejador del cambio de preset de fecha
   */
  const handlePresetChange = useCallback((newPreset) => {
    setFilterState((prev) => {
      if (newPreset === 'custom') {
        return {
          ...prev,
          preset: 'custom',
        };
      }

      const range = GetPresetRange(newPreset, prev.timezone);
      return {
        ...prev,
        preset: newPreset,
        startLocal: range.startLocal,
        endLocal: range.endLocal,
        startUtc: range.startUtc,
        endUtc: range.endUtc,
        isValid: true,
      };
    });
  }, []);

  /**
   * Manejador de cambio en los inputs de fecha y hora local
   */
  const handleDateChange = useCallback(
    (field, value) => {
      setFilterState((prev) => {
        const newStartLocal = field === 'startLocal' ? value : prev.startLocal;
        const newEndLocal = field === 'endLocal' ? value : prev.endLocal;

        const newStartUtc = ConvertLocalToUtc(newStartLocal, prev.timezone);
        const newEndUtc = ConvertLocalToUtc(newEndLocal, prev.timezone);

        const validation = ValidateDateRange(newStartUtc, newEndUtc);

        if (!validation.isValid) {
          showMessage({
            type: MESSAGE_TYPE.WARNING,
            text:
              validation.message ||
              'La fecha/hora de fin debe ser posterior a la de inicio.',
          });
        }

        return {
          ...prev,
          preset: 'custom',
          startLocal: newStartLocal,
          endLocal: newEndLocal,
          startUtc: newStartUtc,
          endUtc: newEndUtc,
          isValid: validation.isValid,
        };
      });
    },
    [showMessage]
  );

  // Estado de descarga para el reporte de Fan Cam ZIP (HU-04)
  const [isDownloadingFanCam, setIsDownloadingFanCam] = useState(false);

  /**
   * Dispara la descarga empaquetada de fotografías de Fan Cam en formato ZIP (HU-04).
   */
  const handleDownloadFanCam = useCallback(async () => {
    if (isDownloadingFanCam) return;

    if (!filterState.isValid) {
      showMessage({
        type: MESSAGE_TYPE.WARNING,
        text: 'Por favor, selecciona un rango de fechas válido antes de descargar.',
      });
      return;
    }

    setIsDownloadingFanCam(true);
    showMessage({
      type: MESSAGE_TYPE.INFO,
      text: 'Generando archivo ZIP de Fan Cam...',
      duration: 4000,
    });

    try {
      const result = await DownloadFanCamZip(filterState);
      showMessage({
        type: MESSAGE_TYPE.SUCCESS,
        text: `Reporte Fan Cam descargado exitosamente: ${result.fileName}`,
        duration: 5000,
      });
    } catch (error) {
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: error.message || 'Ocurrió un error inesperado al descargar el archivo ZIP de Fan Cam.',
        duration: 5000,
      });
    } finally {
      setIsDownloadingFanCam(false);
    }
  }, [filterState, isDownloadingFanCam, showMessage]);

  // Estado de descarga para el reporte de mensajes Excel (HU-03)
  const [isDownloadingMessages, setIsDownloadingMessages] = useState(false);

  /**
   * Dispara la descarga del reporte de mensajes en formato Excel (.xlsx) (HU-03).
   */
  const handleDownloadMessages = useCallback(async () => {
    if (isDownloadingMessages) return;

    if (!filterState.isValid) {
      showMessage({
        type: MESSAGE_TYPE.WARNING,
        text: 'Por favor, selecciona un rango de fechas válido antes de descargar.',
      });
      return;
    }

    setIsDownloadingMessages(true);
    showMessage({
      type: MESSAGE_TYPE.INFO,
      text: 'Generando reporte de Mensajes de Apoyo en Excel...',
      duration: 3500,
    });

    try {
      const result = await DownloadMessagesExcel(filterState);
      if (result.success) {
        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: `Reporte descargado exitosamente: ${result.fileName || 'Mensajes_Apoyo.xlsx'}`,
          duration: 5000,
        });
      } else {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: result.message || 'No fue posible generar el archivo de reporte.',
          duration: 5000,
        });
      }
    } catch (error) {
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: error.message || 'Ocurrió un error inesperado al descargar el reporte.',
        duration: 5000,
      });
    } finally {
      setIsDownloadingMessages(false);
    }
  }, [filterState, isDownloadingMessages, showMessage]);

  // Estado local para el Respaldo Total (Master ZIP) (HU-05)
  const [isGeneratingMaster, setIsGeneratingMaster] = useState(false);
  const [masterProgress, setMasterProgress] = useState({
    percentage: 0,
    currentStep: '',
    itemsProcessed: 0,
    totalItems: 0,
    token: null,
  });

  // Referencias para control de concurrencia y ciclo de vida de polling
  const pollingIntervalRef = useRef(null);
  const completedTokensRef = useRef(new Set());

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Limpieza al desmontar el componente
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Conexión y escuchas de telemetría en tiempo real con SignalR (HU-05)
  useEffect(() => {
    if (!signalRService.isConnected) {
      signalRService.connect({ tipo: 'admin' }).catch(() => {
        // Conexión en segundo plano tolerante a fallos
      });
    }

    const onReportProgress = (data) => {
      if (!data) return;
      const percentage = data.percentage ?? data.Percentage ?? 0;
      const currentStep = data.currentStep || data.CurrentStep || '';
      const itemsProcessed = data.itemsProcessed ?? data.ItemsProcessed ?? 0;
      const totalItems = data.totalItems ?? data.TotalItems ?? 0;
      const token = data.token ?? data.Token;

      setMasterProgress((prev) => ({
        ...prev,
        percentage,
        currentStep: currentStep || prev.currentStep,
        itemsProcessed: itemsProcessed || prev.itemsProcessed,
        totalItems: totalItems || prev.totalItems,
        token: token || prev.token,
      }));
    };

    const onReportCompleted = async (data) => {
      if (!data) return;
      const token = data.token || data.Token;
      if (token && completedTokensRef.current.has(token)) return;
      if (token) completedTokensRef.current.add(token);

      stopPolling();
      setIsGeneratingMaster(false);
      setMasterProgress({
        percentage: 0,
        currentStep: '',
        itemsProcessed: 0,
        totalItems: 0,
        token: null,
      });

      const downloadUrl = data.downloadUrl || data.DownloadUrl;
      const fileName = data.fileName || data.FileName;

      try {
        await DownloadReadyReport(downloadUrl, fileName);
        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: '¡Respaldo Maestro completado y descargado exitosamente!',
          duration: 5000,
        });
      } catch (err) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: err.message || 'Error al descargar el archivo maestro generado.',
          duration: 5000,
        });
      }
    };

    const onReportError = (data) => {
      stopPolling();
      setIsGeneratingMaster(false);
      setMasterProgress({
        percentage: 0,
        currentStep: '',
        itemsProcessed: 0,
        totalItems: 0,
        token: null,
      });

      const message =
        (typeof data === 'string' ? data : (data?.message || data?.Message)) ||
        'Ocurrió un error al generar el reporte maestro.';

      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: message,
        duration: 5000,
      });
    };

    signalRService.on('report_progress', onReportProgress);
    signalRService.on('report_completed', onReportCompleted);
    signalRService.on('report_error', onReportError);

    return () => {
      signalRService.off('report_progress', onReportProgress);
      signalRService.off('report_completed', onReportCompleted);
      signalRService.off('report_error', onReportError);
    };
  }, [showMessage, stopPolling]);

  /**
   * Dispara la generación asíncrona y descarga del Respaldo Maestro (Master ZIP) (HU-05).
   * Valida filtros, emite feedback inicial, solicita el job a la API y activa telemetría
   * vía SignalR con mecanismo de respaldo de polling Zero-Crash.
   */
  const handleDownloadMasterZip = useCallback(async () => {
    if (isGeneratingMaster) return;

    if (!filterState.isValid) {
      showMessage({
        type: MESSAGE_TYPE.WARNING,
        text: 'Por favor, selecciona un rango de fechas válido antes de descargar.',
      });
      return;
    }

    setIsGeneratingMaster(true);
    setMasterProgress({
      percentage: 5,
      currentStep: 'Iniciando empaquetado maestro...',
      itemsProcessed: 0,
      totalItems: 0,
      token: null,
    });

    showMessage({
      type: MESSAGE_TYPE.INFO,
      text: 'Iniciando empaquetado maestro en segundo plano...',
      duration: 4000,
    });

    try {
      const result = await RequestMasterZip(filterState);

      if (!result || !result.success) {
        showMessage({
          type: result?.isConflict ? MESSAGE_TYPE.WARNING : MESSAGE_TYPE.BAD,
          text: result?.message || 'No fue posible iniciar el empaquetado maestro.',
          duration: 5000,
        });
        setIsGeneratingMaster(false);
        setMasterProgress({
          percentage: 0,
          currentStep: '',
          itemsProcessed: 0,
          totalItems: 0,
          token: null,
        });
        return;
      }

      const token = result.token;
      setMasterProgress((prev) => ({ ...prev, token }));

      // Mecanismo de respaldo "Zero-Crash" (polling cada 3s en caso de microcortes de WebSocket)
      stopPolling();
      let jobFinished = false;

      pollingIntervalRef.current = setInterval(async () => {
        if (jobFinished) return;
        try {
          const status = await GetMasterZipStatus(token);
          if (!status || !status.success) return;

          // Si el trabajo ha completado
          if (status.isCompleted || status.status === 'Completed' || status.status === 'completed') {
            jobFinished = true;
            stopPolling();

            if (token && completedTokensRef.current.has(token)) return;
            if (token) completedTokensRef.current.add(token);

            setIsGeneratingMaster(false);
            setMasterProgress({
              percentage: 0,
              currentStep: '',
              itemsProcessed: 0,
              totalItems: 0,
              token: null,
            });

            if (status.downloadUrl) {
              await DownloadReadyReport(status.downloadUrl, status.fileName);
              showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: '¡Respaldo Maestro completado y descargado exitosamente!',
                duration: 5000,
              });
            }
            return;
          }

          // Si el trabajo ha fallado
          if (status.isFaulted || status.status === 'Failed' || status.status === 'failed') {
            jobFinished = true;
            stopPolling();
            setIsGeneratingMaster(false);
            setMasterProgress({
              percentage: 0,
              currentStep: '',
              itemsProcessed: 0,
              totalItems: 0,
              token: null,
            });

            showMessage({
              type: MESSAGE_TYPE.BAD,
              text: status.errorMessage || status.message || 'Ocurrió un error al generar el reporte maestro.',
              duration: 5000,
            });
            return;
          }

          // Actualizar estado de progreso continuo
          setMasterProgress((prev) => ({
            ...prev,
            percentage: status.percentage ?? prev.percentage,
            currentStep: status.currentStep || prev.currentStep,
            itemsProcessed: status.itemsProcessed ?? prev.itemsProcessed,
            totalItems: status.totalItems ?? prev.totalItems,
          }));
        } catch {
          // Ignorar microcortes transitorios en el polling
        }
      }, 3000);
    } catch (error) {
      setIsGeneratingMaster(false);
      setMasterProgress({
        percentage: 0,
        currentStep: '',
        itemsProcessed: 0,
        totalItems: 0,
        token: null,
      });
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: error.message || 'Ocurrió un error inesperado al solicitar el Respaldo Maestro.',
        duration: 5000,
      });
    }
  }, [filterState, isGeneratingMaster, showMessage, stopPolling]);

  return (
    <SecondaryBase actionToExecute={getOutOfTheDynamic}>
      <div className="AdmReports">
        {/* Cabecera Principal */}
        <header className="AdmReports__Header">
          <h2 className="AdmReports__Header__Title">
            <i className="bi bi-file-earmark-bar-graph AdmReports__Header__TitleIcon" aria-hidden="true"></i>
            Reportes y Estadísticas
          </h2>
          <p className="AdmReports__Header__Subtitle">
            Consulta, analiza y exporta las métricas de participación de los aficionados en tiempo real para cada dinámica del evento.
          </p>
        </header>

        {/* Barra Superior de Filtrado Temporal y Selector de Zona Horaria (HU-02) */}
        <DateRangeFilter
          filterState={filterState}
          onModeChange={handleModeChange}
          onPresetChange={handlePresetChange}
          onDateChange={handleDateChange}
          timezoneSelector={
            <TimezoneSelector
              currentTimezone={filterState.timezone}
              onChangeTimezone={handleTimezoneChange}
              detectedTimezone={detectedTimezone}
            />
          }
        />

        {/* Contenedor de Tarjetas de Reportes */}
        <div className="AdmReports__Container">
          {/* Tarjeta 1: Fan Cam (HU-04) */}
          <ReportCard
            title="Fan Cam (Fotografías)"
            icon="bi bi-camera-fill"
            iconVariant="primary"
            badgeText="Disponible"
            badgeVariant="success"
            description="Descarga empaquetada de fotografías capturadas durante el evento, clasificadas automáticamente en subcarpetas y renombradas según la hora local del estadio."
            buttonText="Descargar ZIP de Fan Cam"
            buttonIcon="bi bi-file-earmark-zip"
            buttonVariant="primary"
            onDownload={handleDownloadFanCam}
            isLoading={isDownloadingFanCam}
            isDisabled={!filterState.isValid || isDownloadingFanCam}
          >
            <ul className="AdmReports__Card__Features">
              <li>
                <i className="bi bi-check-circle-fill"></i> Clasificación en subcarpetas: Fan, Fan IA y Staff
              </li>
              <li>
                <i className="bi bi-check-circle-fill"></i> Nomenclatura con hora local del estadio
              </li>
              <li>
                <i className="bi bi-check-circle-fill"></i> Respaldo íntegro (incluye moderadas) sin herramientas externas
              </li>
            </ul>
          </ReportCard>

          {/* Tarjeta 2: Mensajes de Apoyo (HU-03) */}
          <ReportCard
            title="Mensajes de Apoyo"
            icon="bi bi-chat-square-heart"
            iconVariant="blue"
            badgeText="Disponible"
            badgeVariant="success"
            description="Bitácora y volumen de mensajes enviados por la afición, moderación en vivo, palabras clave recurrentes y analítica de engagement."
            buttonText="Descargar Excel (.xlsx)"
            buttonIcon="bi bi-file-earmark-excel"
            buttonVariant="excel"
            onDownload={handleDownloadMessages}
            isLoading={isDownloadingMessages}
            isDisabled={!filterState.isValid || isDownloadingMessages}
          >
            <ul className="AdmReports__Card__Features">
              <li>
                <i className="bi bi-check-circle-fill"></i> Registro histórico de mensajes y remitentes
              </li>
              <li>
                <i className="bi bi-check-circle-fill"></i> Tasa de aprobación vs descarte en moderación
              </li>
              <li>
                <i className="bi bi-check-circle-fill"></i> Exportación completa de comentarios
              </li>
            </ul>
          </ReportCard>

          {/* Tarjeta 3: Respaldo Total (Master ZIP) (HU-05) */}
          <ReportCard
            title="Respaldo Total (Master ZIP)"
            icon="bi bi-archive-fill"
            iconVariant="purple"
            badgeText="Disponible"
            badgeVariant="success"
            description="Empaqueta todas las fotografías clasificadas de Fan Cam y el libro Excel de Mensajes de Apoyo en un único archivo comprimido maestro..."
            buttonText={isGeneratingMaster ? "Empaquetando..." : "Descargar Todo (Master ZIP)"}
            buttonIcon={isGeneratingMaster ? "bi bi-arrow-repeat" : "bi bi-cloud-arrow-down-fill"}
            buttonVariant="purple"
            onDownload={handleDownloadMasterZip}
            isLoading={isGeneratingMaster}
            isDisabled={!filterState.isValid || isGeneratingMaster}
          >
            {isGeneratingMaster ? (
              <div className="AdmReports__Progress">
                <div className="AdmReports__Progress__Header">
                  <span className="AdmReports__Progress__Step">
                    <i className="bi bi-gear-wide-connected me-1"></i>
                    {masterProgress.currentStep || 'Iniciando empaquetado maestro...'}
                  </span>
                  <span className="AdmReports__Progress__Percentage">
                    {masterProgress.percentage}%
                  </span>
                </div>

                <div className="AdmReports__Progress__Track">
                  <div
                    className="AdmReports__Progress__Bar AdmReports__ProgressBar"
                    style={{ width: `${Math.min(Math.max(masterProgress.percentage, 5), 100)}%` }}
                  ></div>
                </div>

                {masterProgress.totalItems > 0 && (
                  <div className="AdmReports__Progress__Counter">
                    <i className="bi bi-layers me-1"></i>
                    {`${masterProgress.itemsProcessed} / ${masterProgress.totalItems}`}
                  </div>
                )}
              </div>
            ) : (
              <ul className="AdmReports__Card__Features">
                <li>
                  <i className="bi bi-check-circle-fill"></i> <strong>1_FanCam/</strong> con subcarpetas Fan, Fan IA y Staff clasificadas
                </li>
                <li>
                  <i className="bi bi-check-circle-fill"></i> <strong>2_MensajesDeApoyo/</strong> con libro Excel con emojis y 2 hojas
                </li>
                <li>
                  <i className="bi bi-check-circle-fill"></i> Telemetría SignalR en vivo y arquitectura de streaming ligera
                </li>
              </ul>
            )}
          </ReportCard>
        </div>
      </div>
    </SecondaryBase>
  );
}

export { ReportsManager };
